/**
 * Money & cent-precision helpers.
 *
 * Every money-bearing value on the CatalogItem / LineItem / LineItemGroup /
 * Scope resources is an **integer number of cents**. Floats are tolerated only
 * at the intake boundary (`dollarsToCents`) and the display boundary
 * (`centsToDollars`); all arithmetic in between operates on integer cents.
 *
 * Rounding follows banker's rounding (round-half-to-even) to avoid the
 * systematic upward bias of round-half-up across many calculations.
 *
 * NOTE: cents are represented as `number` (not `bigint`) — at line-item and
 * catalog magnitudes they sit well within `Number.MAX_SAFE_INTEGER`. Because
 * the input is a float, exact-half detection in {@link roundHalfToEven} uses a
 * small epsilon to absorb IEEE-754 noise.
 */

/** Epsilon used to detect an exact `.5` fractional part despite IEEE-754 noise. */
const HALF_TIE_EPSILON = 1e-9;

/**
 * Rounds a real number to the nearest integer using banker's rounding
 * (round-half-to-even). Exact halves resolve to the nearest even integer;
 * every other value rounds to the nearest integer.
 *
 * @param value - The (possibly fractional) number to round.
 * @returns The nearest integer, with ties resolved to even.
 *
 * @example
 * roundHalfToEven(2.5); // 2 (tie → even)
 * roundHalfToEven(3.5); // 4 (tie → even)
 * roundHalfToEven(2.6); // 3 (no tie)
 */
export function roundHalfToEven(value: number): number {
  const floor = Math.floor(value);
  const frac = value - floor;

  if (frac < 0.5 - HALF_TIE_EPSILON) return floor;
  if (frac > 0.5 + HALF_TIE_EPSILON) return floor + 1;

  // Exact tie — round to the nearest even integer.
  return floor % 2 === 0 ? floor : floor + 1;
}

/**
 * Multiplies an integer-cents amount by a real-valued factor (e.g. a quantity
 * such as `2.5`, or a markup ratio such as `1.27`) and returns integer cents,
 * banker's-rounded.
 *
 * @param cents - Integer-cents base amount.
 * @param factor - Real-valued multiplier (quantity, ratio, …).
 * @returns The integer-cents product.
 *
 * @example
 * multiplyCents(1250, 2.5); // 3125
 */
export function multiplyCents(cents: number, factor: number): number {
  return roundHalfToEven(cents * factor);
}

/**
 * Applies a percentage markup to an integer-cents cost.
 * `price = cost * (1 + markupPercent / 100)`, banker's-rounded to integer cents.
 *
 * @param costCents - Integer-cents cost.
 * @param markupPercent - Markup as a percentage (e.g. `27` for 27%).
 * @returns The integer-cents marked-up price.
 *
 * @example
 * applyMarkupToCents(10000, 25); // 12500
 */
export function applyMarkupToCents(
  costCents: number,
  markupPercent: number,
): number {
  return multiplyCents(costCents, 1 + markupPercent / 100);
}

/**
 * Converts a dollar amount to integer cents (banker's-rounded). Use only at an
 * intake boundary where a dollar value first enters the cents domain.
 *
 * @param dollars - Dollar amount.
 * @returns The equivalent integer cents.
 *
 * @example
 * dollarsToCents(12.5); // 1250
 */
export function dollarsToCents(dollars: number): number {
  return roundHalfToEven(dollars * 100);
}

/**
 * Converts integer cents to a dollar number (`cents / 100`). Use only at a
 * display boundary (currency formatting); never re-enter cent math with the
 * result.
 *
 * @param cents - Integer cents.
 * @returns The dollar value.
 *
 * @example
 * centsToDollars(1250); // 12.5
 */
export function centsToDollars(cents: number): number {
  return cents / 100;
}
